import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, ReactNode } from 'react';
import styles from './PageHeader.module.css';
import { resolveBackTarget } from '../lib/backNavigation';

/**
 * AETERNA PageHeader 组件
 * 从 Mockup PageHeader.js 迁移，支持 4 种模式：
 *   - primary:   一级页面大标题（照见/践行/真知/同行/解惑）
 *   - detail:    二级页面固定导航栏（返回+居中标题+操作按钮）
 *   - immersive: 沉浸式（初始透明，下滑后淡入）
 *   - wizard:    向导流程（步骤+关闭按钮）
 */

/* ── SVG 图标 ── */
const ICONS = {
  back: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7"/>
    </svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  filter: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
    </svg>
  ),
  share: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
    </svg>
  ),
  bookmark: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  search: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
    </svg>
  ),
  profile: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  more: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/>
    </svg>
  ),
  plus: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  cart: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
    </svg>
  ),
  history: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l4 2"/>
    </svg>
  ),
} as const;

export type IconName = keyof typeof ICONS;

/* ── 操作按钮 ── */
export interface HeaderAction {
  icon: IconName;
  label?: string;
  onClick?: () => void;
  id?: string;
}

/* ── 组件 Props ── */
interface PageHeaderBaseProps {
  className?: string;
  children?: ReactNode;
}

interface PrimaryHeaderProps extends PageHeaderBaseProps {
  mode: 'primary';
  title: string;
  subtitle?: string;
  showProfile?: boolean;
  profileHref?: string;
  actions?: HeaderAction[];
  tabs?: string[];
  activeTab?: number;
  onTabChange?: (index: number, label: string) => void;
}

interface DetailHeaderProps extends PageHeaderBaseProps {
  mode: 'detail';
  title: string;
  back?: boolean | string;
  sticky?: boolean;
  scrollHide?: boolean;
  actions?: HeaderAction[];
  tabs?: string[];
  activeTab?: number;
  onTabChange?: (index: number, label: string) => void;
}

interface ImmersiveHeaderProps extends PageHeaderBaseProps {
  mode: 'immersive';
  back?: boolean | string;
  actions?: HeaderAction[];
  progress?: boolean;
}

interface WizardHeaderProps extends PageHeaderBaseProps {
  mode: 'wizard';
  back?: boolean | string;
  currentStep?: number;
  totalSteps?: number;
}

export type PageHeaderProps = PrimaryHeaderProps | DetailHeaderProps | ImmersiveHeaderProps | WizardHeaderProps;

/* ── BackButton 子组件 ── */
function BackButton({ back, iconType = 'back' }: { back?: boolean | string; iconType?: 'back' | 'close' }) {
  const navigate = useNavigate();
  if (back === undefined || back === null) return <div className={styles.spacer} />;

  const handleClick = () => {
    if (typeof back === 'string') {
      navigate(back);
    } else {
      navigate(resolveBackTarget(window.location.pathname));
    }
  };

  return (
    <button onClick={handleClick} className={styles.backBtn} aria-label="返回">
      {ICONS[iconType]}
    </button>
  );
}

/* ── Tabs 子组件 ── */
function Tabs({ tabs, activeTab = 0, onTabChange }: { tabs: string[]; activeTab?: number; onTabChange?: (i: number, label: string) => void }) {
  const [active, setActive] = useState(activeTab);

  // Sync local state when the parent prop changes
  useEffect(() => {
    setActive(activeTab);
  }, [activeTab]);

  return (
    <nav className={styles.tabsNav} role="navigation" aria-label="分类筛选">
      {tabs.map((tab, i) => (
        <button
          key={tab}
          className={i === active ? styles.tabActive : styles.tab}
          aria-pressed={i === active}
          onClick={() => { setActive(i); onTabChange?.(i, tab); }}
        >
          {tab}
        </button>
      ))}
    </nav>
  );
}

/* ── Actions 子组件 ── */
function Actions({ actions }: { actions?: HeaderAction[] }) {
  if (!actions || actions.length === 0) return <div className={styles.spacer} />;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, minWidth: 40, justifyContent: 'flex-end' }}>
      {actions.map((action, i) => (
        <button key={action.id || i} id={action.id} onClick={action.onClick} className={styles.actionBtn} aria-label={action.label || action.icon}>
          {ICONS[action.icon]}
        </button>
      ))}
    </div>
  );
}

/* ════════════════════════════════════════
   Primary Mode — 一级页面大标题
   ════════════════════════════════════════ */
function PrimaryHeader({ className, title, subtitle, showProfile = false, profileHref = '/profile', actions, tabs, activeTab, onTabChange }: PrimaryHeaderProps) {
  const navigate = useNavigate();
  const hasTabs = tabs && tabs.length > 0;
  return (
    <>
      <header className={`${styles.primaryHeader} ${className ?? ''}`} role="banner">
        <div className={styles.primaryInner}>
          <div className={styles.primaryTitleWrap}>
            {showProfile && (
              <button onClick={() => navigate(profileHref)} className={styles.backBtn} aria-label="个人中心">
                {ICONS.profile}
              </button>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center' }}>
              <h1 className={styles.primaryTitle}>
                {title}
              </h1>
              {subtitle && (
                <p className={styles.primarySubtitle}>
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          <Actions actions={actions} />
        </div>
        {hasTabs && <Tabs tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />}
      </header>
      <div className={hasTabs ? styles.primarySpacerWithTabs : styles.primarySpacer} />
    </>
  );
}

/* ════════════════════════════════════════
   Detail Mode — 二级页面导航栏
   ════════════════════════════════════════ */
function DetailHeader({ title, back = true, scrollHide, actions, tabs, activeTab, onTabChange }: DetailHeaderProps) {
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    if (!scrollHide) return;
    const target = document.querySelector('main') ?? window;
    const handler = () => {
      setIsCompact((target as HTMLElement).scrollTop > 60);
    };
    target.addEventListener('scroll', handler, { passive: true });
    return () => target.removeEventListener('scroll', handler);
  }, [scrollHide]);

  const headerClass = `${styles.header} ${scrollHide ? styles.scrollHide : ''} ${scrollHide && isCompact ? styles.scrollHideCompact : ''}`;

  return (
    <>
      <header className={headerClass} role="banner">
        <div className={styles.bar}>
          <BackButton back={back} />
          <div className={styles.center}>
            <h1 className={styles.barTitle}>{title}</h1>
          </div>
          <Actions actions={actions} />
        </div>
        {tabs && tabs.length > 0 && <Tabs tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />}
      </header>
      <div className={styles.detailSpacer} />
    </>
  );
}

/* ════════════════════════════════════════
   Immersive Mode — 沉浸式
   ════════════════════════════════════════ */
function ImmersiveHeader({ back = true, actions, progress }: ImmersiveHeaderProps) {
  const [visible, setVisible] = useState(false);
  const [progressWidth, setProgressWidth] = useState(0);

  const handleScroll = useCallback(() => {
    const scrollEl = document.querySelector('main') ?? document.documentElement;
    const scrollTop = (scrollEl as HTMLElement).scrollTop;
    setVisible(scrollTop > 200);
    if (progress) {
      const docH = scrollEl.scrollHeight - (scrollEl as HTMLElement).clientHeight;
      setProgressWidth(docH > 0 ? (scrollTop / docH) * 100 : 0);
    }
  }, [progress]);

  useEffect(() => {
    const target = document.querySelector('main') ?? window;
    target.addEventListener('scroll', handleScroll, { passive: true });
    return () => target.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const headerClass = `${styles.immersiveHeader} ${visible ? styles.immersiveVisible : ''}`;

  return (
    <>
      {progress && (
        <div className={styles.progressTrack}>
          <div className={styles.progressFill} style={{ width: `${progressWidth}%` }} />
        </div>
      )}
      <header className={headerClass} role="banner">
        <div className={styles.bar}>
          <BackButton back={back} />
          <div className={styles.center} />
          <Actions actions={actions} />
        </div>
      </header>
    </>
  );
}

/* ════════════════════════════════════════
   Wizard Mode — 向导流程
   ════════════════════════════════════════ */
function WizardHeader({ back = true, currentStep = 1, totalSteps = 3 }: WizardHeaderProps) {
  return (
    <>
      <header className={styles.wizardHeader} role="banner">
        <div className={styles.bar}>
          <BackButton back={back} iconType="close" />
          <span className={styles.wizardStep}>
            {currentStep} / {totalSteps}
          </span>
          <span className={styles.wizardHint}>
            自动保存
          </span>
        </div>
      </header>
      <div className={styles.wizardSpacer} />
    </>
  );
}

/* ════════════════════════════════════════
   主入口
   ════════════════════════════════════════ */
export function PageHeader(props: PageHeaderProps) {
  switch (props.mode) {
    case 'primary':
      return <PrimaryHeader {...props} />;
    case 'detail':
      return <DetailHeader {...props} />;
    case 'immersive':
      return <ImmersiveHeader {...props} />;
    case 'wizard':
      return <WizardHeader {...props} />;
    default:
      return <DetailHeader {...(props as DetailHeaderProps)} />;
  }
}

// named export only — 所有页面统一使用 import { PageHeader } from '...'
